import { useId } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import container from 'src/container';
import { Organization } from 'src/domain/organization';
import {
    employeeDepartmentPath,
    LEGACY_DEPARTMENT_VALUE,
} from 'src/utils/employeeDepartment';

const {
    cradle: { munduClient },
} = container;

type Props<T extends FieldValues> = {
    control: Control<T>;
    name: Path<T>;
    organizationId: string;
    legacyDepartment?: string;
    disabled?: boolean;
};

export const EmployeeDepartmentSelect = <T extends FieldValues>({
    control,
    name,
    organizationId,
    legacyDepartment,
    disabled = false,
}: Props<T>) => {
    const { t } = useTranslation();
    const id = useId();
    const { data: organization, error } = useSWR<Organization>(
        organizationId ? ['organization', organizationId] : null,
        (_key, organizationId) => munduClient.getOrganization(organizationId),
    );
    const groups = organization?.groups ?? [];
    const options = groups.filter(group => !group.parentId);

    return (
        <Controller
            control={control}
            name={name}
            render={({ field }) => {
                const value: string = field.value ?? '';
                const isLegacy = value === LEGACY_DEPARTMENT_VALUE;
                const group = groups.find(group => group.id === value);
                const isUnavailable = Boolean(
                    value &&
                        !isLegacy &&
                        !options.some(option => option.id === value),
                );
                const help = error
                    ? t('creators.form.departmentLoadError')
                    : !organization
                    ? t('common.loading')
                    : isLegacy
                    ? t('creators.form.departmentLegacyHelp')
                    : isUnavailable
                    ? t(
                          group
                              ? 'creators.form.departmentNestedHelp'
                              : 'creators.form.departmentRemovedHelp',
                      )
                    : t('creators.form.departmentHelp');

                return (
                    <div>
                        <label htmlFor={id} className="text-sm mb-1.5 block">
                            {t('creators.form.departmentLabel')}
                        </label>
                        <select
                            id={id}
                            name={field.name}
                            ref={field.ref}
                            value={value}
                            onBlur={field.onBlur}
                            onChange={field.onChange}
                            disabled={
                                disabled || !organization || Boolean(error)
                            }
                            aria-describedby={`${id}-help`}
                            className="text-input w-full"
                        >
                            <option value="">
                                {t('creators.form.noDepartment')}
                            </option>
                            {isLegacy && (
                                <option
                                    value={LEGACY_DEPARTMENT_VALUE}
                                    disabled
                                >
                                    {t('creators.form.departmentLegacy', {
                                        value: legacyDepartment,
                                    })}
                                </option>
                            )}
                            {isUnavailable && (
                                <option value={value} disabled>
                                    {!organization
                                        ? t(
                                              error
                                                  ? 'creators.form.departmentLoadError'
                                                  : 'common.loading',
                                          )
                                        : group
                                        ? employeeDepartmentPath(
                                              group.id,
                                              groups,
                                          )
                                        : t('creators.form.departmentRemoved')}
                                </option>
                            )}
                            {options.map(group => (
                                <option key={group.id} value={group.id}>
                                    {employeeDepartmentPath(group.id, groups)}
                                </option>
                            ))}
                        </select>
                        <p
                            id={`${id}-help`}
                            className="text-sm text-mongooseGray mt-1"
                        >
                            {help}
                        </p>
                    </div>
                );
            }}
        />
    );
};
